/**
 * RuleEditDialog — create / edit one Soll-Deckung rule (P2 Placement Slice 3).
 *
 * Slice 3 edits the DE label + ziel_text + pflicht_status + rechtsgrundlage + Zielsumme + Prio (+ the
 * create-only identity ebene/rule_key/branchencode/kundentyp). FR/IT/EN labels + typ/options editing
 * are Slice 3b. On save → POST (create) / PATCH (edit); the error code surfaces inline.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  SOLL_PFLICHT_STATUS_VALUES,
  SOLL_PFLICHT_STATUS_LABELS,
  type RegelwerkSollDeckungItem,
  type SollDeckungRuleCreateBody,
  type SollDeckungRulePatchBody,
  type SollPflichtStatus,
} from '@brokly/shared';
import { apiClient } from '../../../../lib/api-client';
import { Button, Dialog, TextInput } from '../../components';

type Props =
  | { mode: 'create'; onClose: () => void; onSaved: () => void; rule?: undefined }
  | { mode: 'edit'; rule: RegelwerkSollDeckungItem; onClose: () => void; onSaved: () => void };

export function RuleEditDialog(props: Props) {
  const edit = props.mode === 'edit' ? props.rule : null;
  const [ebene, setEbene] = useState<string>(edit?.ebene ?? 'police');
  const [ruleKey, setRuleKey] = useState(edit?.rule_key ?? '');
  const [branchencode, setBranchencode] = useState(edit?.branchencode ?? '');
  const [kundentyp, setKundentyp] = useState<string>(edit?.kundentyp ?? '');
  const [label, setLabel] = useState(edit?.label_i18n.de ?? '');
  const [pflicht, setPflicht] = useState<SollPflichtStatus>(edit?.pflicht_status ?? 'empfehlung');
  const [recht, setRecht] = useState(edit?.rechtsgrundlage ?? '');
  const [zielText, setZielText] = useState(edit?.ziel_text_i18n.de ?? '');
  const [zielSumme, setZielSumme] = useState(edit?.ziel_deckungssumme ?? '');
  const [prio, setPrio] = useState(String(edit?.prioritaet ?? 100));
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const prioritaet = Number(prio);
      if (!Number.isInteger(prioritaet) || prioritaet < 1 || prioritaet > 999) {
        throw new Error('Prioritaet muss zwischen 1 und 999 liegen.');
      }
      const zielDeckungssumme = zielSumme.trim() === '' ? null : Number(zielSumme);
      if (zielDeckungssumme !== null && !(zielDeckungssumme >= 0)) {
        throw new Error('Zielsumme muss eine Zahl >= 0 sein.');
      }
      if (props.mode === 'create') {
        if (!/^[a-z0-9_]{2,60}$/.test(ruleKey)) throw new Error('Regel-Schluessel: 2-60 Zeichen a-z 0-9 _.');
        if (!/^\d{5}$/.test(branchencode)) throw new Error('Branchencode: 5 Ziffern.');
        const body: SollDeckungRuleCreateBody = {
          ebene: ebene as 'police' | 'kunde',
          rule_key: ruleKey,
          branchencode,
          kundentyp: ebene === 'kunde' ? (kundentyp as 'firma' | 'privatperson') : null,
          label_i18n: { de: label },
          pflicht_status: pflicht,
          rechtsgrundlage: recht.trim() === '' ? null : recht,
          ziel_text_i18n: { de: zielText },
          ziel_deckungssumme: zielDeckungssumme,
          prioritaet,
        };
        return apiClient.admin.createRegelwerkSollRule(body);
      }
      const body: SollDeckungRulePatchBody = {
        label_i18n: { de: label },
        pflicht_status: pflicht,
        rechtsgrundlage: recht.trim() === '' ? null : recht,
        ziel_text_i18n: { de: zielText },
        ziel_deckungssumme: zielDeckungssumme,
        prioritaet,
        expected_updated_at: edit!.updated_at,
      };
      return apiClient.admin.patchRegelwerkSollRule(edit!.id, body);
    },
    onSuccess: () => props.onSaved(),
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Dialog
      open
      onClose={props.onClose}
      title={props.mode === 'create' ? 'Neue Regel' : 'Regel bearbeiten'}
      wide
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={props.onClose}>Abbrechen</Button>
          <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()} data-testid="regelwerk-rule-save">
            Speichern
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {props.mode === 'create' && (
          <>
            <Field label="Ebene">
              <select value={ebene} onChange={(e) => setEbene(e.target.value)} aria-label="Ebene" className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm">
                <option value="police">Police</option>
                <option value="kunde">Kunde</option>
              </select>
            </Field>
            {ebene === 'kunde' && (
              <Field label="Kundentyp">
                <select value={kundentyp} onChange={(e) => setKundentyp(e.target.value)} aria-label="Kundentyp" className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm">
                  <option value="">-</option>
                  <option value="firma">Firma</option>
                  <option value="privatperson">Privatperson</option>
                </select>
              </Field>
            )}
            <Field label="Regel-Schluessel">
              <TextInput value={ruleKey} onChange={(e) => setRuleKey(e.target.value)} aria-label="Regel-Schluessel" />
            </Field>
            <Field label="Branchencode">
              <TextInput value={branchencode} onChange={(e) => setBranchencode(e.target.value)} aria-label="Branchencode" />
            </Field>
          </>
        )}
        <Field label="Bezeichnung (DE)">
          <TextInput value={label} onChange={(e) => setLabel(e.target.value)} aria-label="Bezeichnung" />
        </Field>
        <Field label="Pflicht-Status">
          <select value={pflicht} onChange={(e) => setPflicht(e.target.value as SollPflichtStatus)} aria-label="Pflicht-Status" className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm">
            {SOLL_PFLICHT_STATUS_VALUES.map((v) => (
              <option key={v} value={v}>{SOLL_PFLICHT_STATUS_LABELS[v]}</option>
            ))}
          </select>
        </Field>
        <Field label="Ziel-Text (DE)">
          <textarea value={zielText} onChange={(e) => setZielText(e.target.value)} aria-label="Ziel-Text" rows={3} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
        </Field>
        <Field label="Rechtsgrundlage">
          <TextInput value={recht} onChange={(e) => setRecht(e.target.value)} aria-label="Rechtsgrundlage" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Zielsumme (CHF)">
            <TextInput inputMode="numeric" value={zielSumme} onChange={(e) => setZielSumme(e.target.value)} aria-label="Zielsumme" />
          </Field>
          <Field label="Prioritaet">
            <TextInput inputMode="numeric" value={prio} onChange={(e) => setPrio(e.target.value)} aria-label="Prioritaet" />
          </Field>
        </div>
        {error != null && <div className="text-sm text-status-danger" data-testid="regelwerk-rule-error">{error}</div>}
      </div>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
